import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Copy, Check, Apple, Bot, FileText } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import {
  ANDROID_PACKAGE,
  ANDROID_PERMISSIONS,
  ANDROID_REVIEW_NOTES,
  ANDROID_VERSION_CODE,
  APPLE_REVIEW_NOTES,
  APP_BUNDLE_HOSTS,
  APP_BUNDLE_VERSION,
  APP_STORE_PRIVACY,
  IOS_BUNDLE_ID,
  IOS_PERMISSIONS,
  PLAY_DATA_SAFETY,
  STORE_LISTING,
} from "@/app-bridge/functions/app-bundle";


export const Route = createFileRoute("/_authenticated/admin/butiksunderlag")({
  head: () => ({
    meta: [
      { title: "Butiksunderlag — Sellavo admin" },
      {
        name: "description",
        content:
          "Färdiga svar för App Store Privacy och Google Play Data Safety, samt texter till butikssidorna.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StoreDocsPage,
});

// Texterna bor i app-bryggan så att butikssidan, adminsidan och det öppna
// app-underlaget alltid säger exakt samma sak.

const IOS_STRINGS = `iOS – texter i Info.plist

${IOS_PERMISSIONS.filter((p) => p.text)
  .map((p) => `${p.key}\n${p.text}`)
  .join("\n\n")}

Associated Domains: ${APP_BUNDLE_HOSTS.map((h) => `applinks:${h}`).join(", ")}
Bundle ID: ${IOS_BUNDLE_ID} (Team ID fylls i i app-bundle.ts)`;

const ANDROID_STRINGS = `Android – manifest och motiveringar

Paketnamn: ${ANDROID_PACKAGE}
minSdk 26, targetSdk 35, versionCode ${ANDROID_VERSION_CODE}, versionName ${APP_BUNDLE_VERSION}

Behörigheter och motivering:
${ANDROID_PERMISSIONS.map((p) => `${p.key}\n${p.purpose}${p.optional ? " (frivillig)" : ""}`).join("\n\n")}

App Links (intent-filter, autoVerify="true"):
${APP_BUNDLE_HOSTS.map((h) => `https://${h}`).join("\n")}
Verifieras mot https://sellavo.com/.well-known/assetlinks.json
SHA-256 hämtas i Play Console → Setup → App signing och läggs i app-bundle.ts.

Adaptiv ikon: förgrund /icons/android-adaptive-foreground-432.png, bakgrund #2d8a9e.
Notisikon: /icons/android-96.png (vit siluett renderas av skalet).`;

const STORE_LISTING_TEXT = `Butikstext

Namn: ${STORE_LISTING.name}
Undertitel (iOS, max 30 tecken): ${STORE_LISTING.subtitle}
Kort beskrivning (Play, max 80 tecken): ${STORE_LISTING.shortDescription}

Beskrivning:
${STORE_LISTING.description}

Nyckelord: ${STORE_LISTING.keywords.join(", ")}

Support-URL: https://sellavo.com/support
Integritetspolicy: https://sellavo.com/integritetspolicy
Användarvillkor: https://sellavo.com/anvandarvillkor
Radera konto: https://sellavo.com/konto`;

const BLOCKS: { id: string; title: string; icon: React.ReactNode; text: string }[] = [
  { id: "apple", title: "App Store – App Privacy", icon: <Apple className="h-4 w-4" />, text: APP_STORE_PRIVACY },
  { id: "play", title: "Google Play – Data Safety", icon: <Bot className="h-4 w-4" />, text: PLAY_DATA_SAFETY },
  {
    id: "review-ios",
    title: "Granskning – App Store",
    icon: <Apple className="h-4 w-4" />,
    text: APPLE_REVIEW_NOTES,
  },
  {
    id: "review-android",
    title: "Granskning – Google Play",
    icon: <Bot className="h-4 w-4" />,
    text: ANDROID_REVIEW_NOTES,
  },
  { id: "ios", title: "iOS – behörighetstexter", icon: <Apple className="h-4 w-4" />, text: IOS_STRINGS },
  { id: "android", title: "Android – manifest och motiveringar", icon: <Bot className="h-4 w-4" />, text: ANDROID_STRINGS },
  { id: "listing", title: "Butikstext och nyckelord", icon: <FileText className="h-4 w-4" />, text: STORE_LISTING_TEXT },
];


function StoreDocsPage() {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000);
    } catch {
      /* ignoreras */
    }
  };

  return (
    <AdminShell title="Butiksunderlag">
      <p className="mb-2 text-sm text-muted-foreground">
        Färdiga svar och texter att klistra in i App Store Connect och Google Play Console.
      </p>
      <p className="mb-4 text-sm text-muted-foreground">
        Ikoner, färger, behörigheter och de adresser appskalet hämtar finns på{" "}
        <Link to="/admin/appunderlag" className="font-medium text-primary underline underline-offset-2">
          Appunderlag
        </Link>
        .
      </p>
      <div className="space-y-4">
        {BLOCKS.map((block) => (
          <section key={block.id} className="rounded-xl border border-border bg-card p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold">
                <span className="text-primary">{block.icon}</span>
                {block.title}
              </h2>
              <Button size="sm" variant="outline" onClick={() => void copy(block.id, block.text)}>
                {copied === block.id ? (
                  <>
                    <Check className="h-4 w-4" /> Kopierat
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Kopiera
                  </>
                )}
              </Button>
            </div>
            <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
              {block.text}
            </pre>
          </section>
        ))}
      </div>
    </AdminShell>
  );
}
